import React, { useRef, useMemo } from "react";
import { menuNavigationInterface } from "../../types/setting.type";
import { useLocation } from "react-router-dom";
import { BASE_IMAGE_URL } from "../../config/apiConfig";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../store/slices/authSlice";
import useNavigateWithLang from "../../hooks/useNavigateWithLang";
import { useLanguage } from "../../hooks/useLanguage";
import { getCurrentLanguageFromUrl } from "../../utils/languageUtils";

interface DrawerProps {
  navbar: menuNavigationInterface[];
  isOpen: boolean;
  onClose: () => void;
}

const Drawer: React.FC<DrawerProps> = ({ navbar, isOpen, onClose }) => {
  const location = useLocation();
  const drawerRef = useRef<HTMLDivElement>(null);
  const token = useSelector((state: any) => state.auth.token);
  const dispatch = useDispatch();
  const { navigateByName } = useNavigateWithLang();
  const {   changeLanguage } = useLanguage();
      const lang = getCurrentLanguageFromUrl();
   
   // Lọc menu theo trạng thái đăng nhập
  const filteredNavbar = useMemo(() => {
    return token
      ? navbar.filter(
          (item) => (item as any).isLogin === true || !("isLogin" in item)
        )
      : navbar
          .filter(
            (item) => (item as any).isLogin === false || !("isLogin" in item)
          )
          .filter((item) => item.name !== "chat");
  }, [token, navbar]);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      drawerRef.current &&
      !drawerRef.current.contains(event.target as Node)
    ) {
      onClose();
    }
  };

  // Render block items
  const renderBlockItem = (item: any) => {
    switch (item.name) {
      case "logo":
        return (
          <div key={item.name} className="mb-4">
            {item.icon && (
              <img
                src={`${BASE_IMAGE_URL}${item.icon}`}
                alt={item.label}
                className="w-full h-auto mb-2"
              />
            )}
            {item.label && (
              <div className="text-white text-sm mb-2">{item.label}</div>
            )}
          </div>
        );
      case "login":
        return (
          <div key={item.name} className="mb-4">
            {item.icon && (
              <img
                src={`${BASE_IMAGE_URL}${item.icon}`}
                alt={item.label}
                className="h-10 w-10 mb-2 rounded-full"
              />
            )}
            {item.label && (
              <div className="text-white text-sm mb-4">{item.label}</div>
            )}
            {/* Render các button đăng nhập/đăng ký */}
            {Array.isArray(item.items) && (
              <div className="flex gap-2">
                {item.items.map((btn: any) => (
                  <button
                    key={btn.name}
                    onClick={() => {
                      if (btn.name === "login") {
                        window.location.href = `https://phim3s.net/${lang}/user/login/`;
                      } else if (btn.name === "register") {
                        window.location.href = `https://phim3s.net/${lang}/user/register/`;
                      }
                      onClose();
                    }}
                    className={`px-4 py-2 rounded-full text-white font-semibold ${
                      btn.color === "red" ? "bg-red-600" : "transparent"
                    }`}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      case "user":
        return (
          <div key={item.name} className="mb-4">
            {item.icon && (
              <img
                src={`${BASE_IMAGE_URL}${item.icon}`}
                alt={item.label}
                className="h-10 w-10 mb-2 rounded-full"
              />
            )}
            {item.label && (
              <div className="text-white text-sm mb-2">{item.label}</div>
            )}
            {/* Render các icon membership */}
            {item.items && !Array.isArray(item.items) && (
              <div className="flex gap-2 mt-2">
                {Object.values(item.items).map((icon: any, idx: number) => (
                  <div key={idx} className="flex flex-col items-center">
                    <img
                      src={`${BASE_IMAGE_URL}${icon.icon}`}
                      alt={icon.label}
                      className="h-8 w-8"
                    />
                    <span className="text-xs text-white">{icon.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      default:
        return (
          <div key={item.name} className="mb-4">
            {item.icon && (
              <img
                src={`${BASE_IMAGE_URL}${item.icon}`}
                alt={item.label}
                className="h-10 w-10 mb-2 rounded-full"
              />
            )}
            {item.label && (
              <div className="text-white text-sm mb-2">{item.label}</div>
            )}
            {/* Render các button nếu có */}
            {Array.isArray(item.items) && (
              <div className="flex gap-2">
                {item.items.map((btn: any) => (
                  <button
                    key={btn.name}
                    className={`px-3 py-1 rounded text-white ${
                      btn.color === "red" ? "bg-red-600" : "bg-blue-600"
                    }`}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>
            )}
            {/* Render các icon membership nếu có */}
            {item.items && !Array.isArray(item.items) && (
              <div className="flex gap-2 mt-2">
                {Object.values(item.items).map((icon: any, idx: number) => (
                  <div key={idx} className="flex flex-col items-center">
                    <img
                      src={`${BASE_IMAGE_URL}${icon.icon}`}
                      alt={icon.label}
                      className="h-8 w-8"
                    />
                    <span className="text-xs text-white">{icon.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
    }
  };

  // Render space items
  const renderSpaceItem = (item: any) => {
    return (
      <div key={item.name || Math.random()} style={{ height: item.height }} />
    );
  };

  // Render menu items
  const renderMenuItem = (item: any) => {
    // Special case for language menu which has submenu items
    if (item.name === "languages") {
      return (
        <li key={item.name} className="cursor-pointer">
          <div className="block text-sm text-white">
            <div className="flex items-center gap-2">
              {item.icon && (
                <img
                  src={`${BASE_IMAGE_URL}${item.icon}`}
                  alt={item.label}
                  className="h-6 w-6"
                />
              )}
              <span>{item.label}</span>
            </div>
          </div>
          {Array.isArray(item.items) && (
            <ul className="ml-8 mt-2 space-y-2">
              {item.items.map((subItem: any) => (
                <li
                  key={subItem.name}
                  onClick={() => {
                    // Handle language change here
                    changeLanguage(subItem.name);
                    onClose();
                  }}
                  className={`flex items-center gap-2 text-sm ${
                    lang === subItem.name ? "text-primary" : "text-white"
                  }`}
                >
                  {subItem.icon && (
                    <img
                      src={`${BASE_IMAGE_URL}${subItem.icon}`}
                      alt={subItem.label}
                      className="h-4 w-4"
                    />
                  )}
                  <span>{subItem.label}</span>
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    }

    const handleNavigation = () => {
      switch (item.name) {
        case "chat":
          window.open("https://chat.phim3s.net", "_blank");
          break;
        case "movie":
          navigateByName("movies");
          break;
        case "comic":
          navigateByName("comics");
          break;
        case "game":
          navigateByName("games");
          break;
        case "savedContents":
          navigateByName("saved");
          break;
        case "historyContents":
          navigateByName("history");
          break;
        case "logout":
          dispatch(logout());
          break;
        default:
          console.warn(`Unhandled menu item: ${item.name}`);
      }
      onClose();
    };

    return (
      <li key={item.name} onClick={handleNavigation} className="cursor-pointer">
        <div className={`block text-sm ${location.pathname === item.url ? "text-primary" : "text-white"}`}>
          <div className="flex items-center gap-2">
            {item.icon && (
              <img
                src={`${BASE_IMAGE_URL}${item.icon}`}
                alt={item.label}
                className="h-6 w-6"
              />
            )}
            <span>{item.label}</span>
          </div>
        </div>
      </li>
    );
  };

  // Render line items
  const renderLineItem = (item: any) => {
    return (
      <hr
        key={item.name}
        style={{ borderColor: item.color || "#292929" }}
        className="my-2"
      />
    );
  };

  // Main render function that delegates to specific render functions
  const renderDrawerItem = (item: any) => {
    switch (item.type) {
      case "block":
        return renderBlockItem(item);
      case "space":
        return renderSpaceItem(item);
      case "menu":
        return renderMenuItem(item);
      case "line":
        return renderLineItem(item);
      default:
        return null;
    }
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={handleOverlayClick}
        ></div>
      )}

      {/* Drawer */}
      <div
        ref={drawerRef}
        style={{
          display: "flex",
          flexDirection: "column",
          // justifyContent: 'center',
          // alignItems: 'center',
          padding: "0px",
          // gap: '16px',
          width: "320px",
          height: "885px",
          background:
            "linear-gradient(180deg, rgba(209, 16, 48, 0.72) 0%, rgba(1, 1, 1, 0.72) 100%)",
          // boxShadow: "inset -8px 0px 24px rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(5px)", // Note: limited browser support
          flex: "none",
          order: 0,
          flexGrow: 0,
        }}
        className={`fixed top-0 b-0 left-0 h-full w-64  z-50 transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } overflow-y-auto`}
      >
        <div className="p-4">
          <ul className="space-y-4">{filteredNavbar.map(renderDrawerItem)}</ul>
        </div>
      </div>
    </>
  );
};

export default Drawer;
